import React, { useEffect, useMemo, useRef } from 'react';
import { DoubleSide, InstancedMesh, Matrix4, MeshBasicMaterial } from 'three';

import { PlateGeometryType, PlateType } from '@paragon/contract-library';
import { buildPlateShape } from '@paragon/three-shared';

const PLATE_MATERIAL = new MeshBasicMaterial({ color: 0xa0a0a0, transparent: false, opacity: 1.0, side: DoubleSide });

type PlateProportions = {
  readonly width: number;
  readonly length: number;
  readonly thickness: number;
  readonly plateType: PlateType;
  readonly geometryType: PlateGeometryType;
  readonly hingeAngle: number | null;
};

type InstancedPlatesMeshProps = PlateProportions & { simplified?: boolean; matrices: Matrix4[] };

/**
 * An `InstancedMesh` specialized for rendering plates. Geometry will be constructed as-needed; just give the plate's
 *   proportions and a list of transformation matrices.
 *
 * Note that given matrices must be in absolute / world coordinates, and mutating them after rendering this component
 *   will not accomplish anything.
 */
export function InstancedPlateMesh({
  width,
  length,
  plateType,
  geometryType,
  thickness,
  hingeAngle,
  matrices,
  simplified,
}: InstancedPlatesMeshProps) {
  const mesh = useRef<InstancedMesh>(null);
  const geometrySize = useStableInstancedMeshSize(matrices.length, { increment: 100 });

  useEffect(() => {
    if (mesh.current == null) return;

    // update instance transformation matrices
    for (let i = 0; i < matrices.length; i++) {
      const instance = matrices[i];
      mesh.current.setMatrixAt(i, instance);
    }

    // ensure currently-unused instances will not be rendered
    mesh.current.count = matrices.length;

    // ensure stale bounds don't stick around
    if (mesh.current.boundingSphere) mesh.current.boundingSphere = null;
    if (mesh.current.boundingBox) mesh.current.boundingBox = null;

    // ensure three will upload the updated geometry
    mesh.current.instanceMatrix.needsUpdate = true;
  }, [matrices]);

  return (
    <>
      <instancedMesh ref={mesh} args={[undefined, undefined, geometrySize]}>
        <PlateGeometry
          simple={simplified}
          width={width}
          length={length}
          plateType={plateType}
          geometryType={geometryType}
          thickness={thickness}
          hingeAngle={hingeAngle}
        />
        <primitive object={PLATE_MATERIAL} attach="material" dispose={null} />
      </instancedMesh>
    </>
  );
}

/**
 * Maintains a stable item count that will never shrink and always be a multiple of the given increment.
 */
function useStableInstancedMeshSize(currentCount: number, options: { increment: number }) {
  const minCount = useRef(currentCount);
  if (currentCount > minCount.current) minCount.current = currentCount;

  const result = Math.ceil(minCount.current / options.increment) * options.increment;
  if (result > minCount.current) minCount.current = result;

  return result;
}

type PlateGeometryProps = PlateProportions & { simple?: boolean };

/*
 * Magical `ExtrudeGeometry` for plate pairs.
 */
function PlateGeometry({ width, length, plateType, geometryType, thickness, hingeAngle, simple }: PlateGeometryProps) {
  const simpleShape = useMemo(
    () => buildPlateShape({ width, length, plateType, geometryType, hingeAngle }, { slots: !simple }),
    [width, length, plateType, geometryType, hingeAngle, simple],
  );
  const extrusionOptions = useMemo(() => ({ depth: thickness, bevelEnabled: false }), [thickness]);

  return <extrudeGeometry args={[simpleShape, extrusionOptions]} />;
}
